import React from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import ConversationPanel from "./ConversationPanel";
import CallVideoTiles from "./CallVideoTiles";
import TimeWarningModal from "../../../components/TimeWarningModal";
import { CallWebViewProps } from "../call.types";
import MicIcon from "../../../assets/mic-icon";
import CameraIcon from "../../../assets/camera-icon";
import BlurIcon from "../../../assets/blur-icon";
import AdviceIcon from "../../../assets/advice-icon";

export default function CallWebView({
  roomName,
  channelLabel,
  participantsCount,
  calling,
  mediaPermissionError,
  isCheckingPermissions,
  isFetchingToken,
  userName,
  remoteUsers,
  remoteNames,
  localMicrophoneTrack,
  localCameraTrack,
  micOn,
  cameraOn,
  isBlurEnabled,
  isConversationPanelOpen,
  isTimeWarningModalVisible,
  onRetryPermissions,
  onToggleMic,
  onToggleCamera,
  onToggleBlur,
  onToggleConversationPanel,
  onCloseConversationPanel,
  onCloseTimeWarning,
  onFlagUser,
}: CallWebViewProps) {
  return (
    <View style={styles.page}>
      {/* Center states */}
      {!calling ? (
        <View style={styles.center}>
          {mediaPermissionError ? (
            <>
              <Text style={styles.errorText}>{mediaPermissionError}</Text>
              <TouchableOpacity
                style={styles.retry}
                onPress={onRetryPermissions}
                disabled={isCheckingPermissions}
              >
                <Text style={styles.btnText}>
                  {isCheckingPermissions ? "Checking..." : "Retry"}
                </Text>
              </TouchableOpacity>
            </>
          ) : isFetchingToken ? (
            <>
              <ActivityIndicator />
              <Text style={styles.centerText}>
                Fetching room credentials...
              </Text>
            </>
          ) : (
            <>
              <ActivityIndicator />
              <Text style={styles.centerText}>Connecting...</Text>
            </>
          )}
        </View>
      ) : (
        <>
          {isConversationPanelOpen ? (
            <View style={styles.contentRow}>
              {/* Video grid */}
              <ScrollView
                contentContainerStyle={styles.gridColumn}
                style={styles.videoColumn}
                horizontal={false}
                showsVerticalScrollIndicator={false}
              >
                <CallVideoTiles
                  variant="compact"
                  userName={userName}
                  remoteUsers={remoteUsers}
                  remoteNames={remoteNames}
                  localMicrophoneTrack={localMicrophoneTrack}
                  localCameraTrack={localCameraTrack}
                  micOn={micOn}
                  cameraOn={cameraOn}
                  onFlagUser={onFlagUser}
                />
              </ScrollView>

              <ConversationPanel onClose={onCloseConversationPanel} />
            </View>
          ) : (
            <ScrollView
              contentContainerStyle={styles.gridRowCentered}
              style={styles.videoRow}
              horizontal={true}
              showsHorizontalScrollIndicator={false}
            >
              <CallVideoTiles
                variant="default"
                userName={userName}
                remoteUsers={remoteUsers}
                remoteNames={remoteNames}
                localMicrophoneTrack={localMicrophoneTrack}
                localCameraTrack={localCameraTrack}
                micOn={micOn}
                cameraOn={cameraOn}
                onFlagUser={onFlagUser}
              />
            </ScrollView>
          )}

          {/* Controls */}
          <View style={styles.controls}>
            <TouchableOpacity
              style={[styles.iconCtrl, !micOn && styles.iconCtrlOff]}
              onPress={onToggleMic}
              accessibilityRole="button"
              accessibilityLabel={
                micOn ? "Mute microphone" : "Unmute microphone"
              }
            >
              <MicIcon width={59} height={59} off={!micOn} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.iconCtrl, !cameraOn && styles.iconCtrlOff]}
              onPress={onToggleCamera}
              accessibilityRole="button"
              accessibilityLabel={
                cameraOn ? "Turn camera off" : "Turn camera on"
              }
            >
              <CameraIcon width={48} height={48} off={!cameraOn} />
            </TouchableOpacity>
            <TouchableOpacity style={styles.iconCtrl} onPress={onToggleBlur}>
              <BlurIcon
                width={58}
                height={58}
                style={!isBlurEnabled && styles.iconOff}
              />
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.iconCtrl,
                isConversationPanelOpen && styles.iconCtrlActive,
              ]}
              onPress={onToggleConversationPanel}
            >
              <AdviceIcon width={55} height={55} />
            </TouchableOpacity>
          </View>
        </>
      )}
      <TimeWarningModal
        visible={isTimeWarningModalVisible}
        onClose={onCloseTimeWarning}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: "#232335",
    padding: 12,
    paddingBottom: 140,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 6,
  },
  headerTitle: { fontWeight: "700", fontSize: 16, color: "#fff" },
  headerSub: { color: "#fff", fontSize: 12, marginTop: 2 },

  participantsBlock: {
    alignSelf: "flex-start",
    backgroundColor: "#1f1f1f",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 8,
    marginBottom: 10,
  },
  participantsBlockText: { color: "#fff", fontWeight: "600", fontSize: 13 },

  center: { alignItems: "center", justifyContent: "center", padding: 24 },
  centerText: { color: "#666", marginTop: 8, fontSize: 14 },

  gridRowCentered: {
    paddingVertical: 8,
    flexDirection: "row",
    flexWrap: "nowrap",
    justifyContent: "center",
    alignItems: "center",
    flexGrow: 1,
    gap: 12,
  },
  videoRow: {
    width: "100%",
  },
  gridColumn: {
    paddingVertical: 8,
    flexDirection: "column",
    flexWrap: "nowrap",
    justifyContent: "flex-start",
    gap: 12,
  },
  contentRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 24,
  },
  videoColumn: {
    flexGrow: 0,
  },
  controls: {
    position: "absolute",
    left: 12,
    right: 12,
    bottom: 24,
    flexDirection: "row",
    gap: 54,
    justifyContent: "center",
    flexWrap: "wrap",
    zIndex: 2,
  },
  iconCtrl: {
    width: 98,
    height: 98,
    backgroundColor: "#3A6FB1",
    borderRadius: 1000,
    justifyContent: "center",
    alignItems: "center",
  },
  iconCtrlActive: {
    backgroundColor: "#D63C8A",
  },
  // Muted mic / camera off: red with a slashed icon, as in Zoom/Meet (#142).
  iconCtrlOff: {
    backgroundColor: "#E5484D",
  },
  iconOff: {
    opacity: 0.4,
  },

  errorText: {
    color: "#d32f2f",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  retry: {
    marginTop: 12,
    backgroundColor: "#1e88e5",
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  btnText: { color: "#fff", fontWeight: "700" },
});
